import {
  CartCouponData,
  CartItemViewData,
  CartPaymentData,
  CartResponse,
  CartRewardData,
  ItemTaxViewData,
  PriceModifierViewData,
  PriceViewData,
  TareQuantity,
  TenderEligibleAmounts,
} from 'src/items-list/business-logic/service/interfaces/cart-response';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { EJRecordTypes } from '../enumerations/ej-record-types';
import { LineItemStatus } from '../enumerations/line-item-status';
import { PromotionRewardType } from '../enumerations/promotion-reward-types';
import { TenderStatus } from '../enumerations/tender-status';
import { TenderType } from '../enumerations/tender-type';
import { UnitOfMeasureTypes } from '../enumerations/unit-of-measure-type';
import { convertCurrency, convertUnitPriceCurrency } from '../helpers/currency-helper';
import { LOCAL_CURRENCY_SYMBOL } from '../helpers/localized-description-helper';
import { getValueAsNumber } from '../helpers/number-helper';
import { IRedemptionState } from '../interfaces/dtos/ILineCoupon';
import { ILineItem } from '../interfaces/dtos/ILineItem';
import { ILineReward } from '../interfaces/dtos/ILineReward';
import { IPricedQuantity } from '../interfaces/dtos/IPricedQuantity';
import { IPriceModifierInfo } from '../interfaces/dtos/IPriceModifierInfo';
import { ITareWeight } from '../interfaces/dtos/ITareWeight';
import { ITax } from '../interfaces/dtos/ITax';
import {
  IElectronicJournalRecord,
  IEligibilityInfo,
  OnlineItem,
} from '../interfaces/states/IElectronicJournalRecord';
import { store } from '../redux/configure-store';

export const convertCartResponse = (cartResponse: CartResponse): IElectronicJournalRecord[] => {
  let activeTenders: CartPaymentData[] = [];
  if (cartResponse.payments) {
    activeTenders = cartResponse.payments?.filter(
      payment => payment.sequenceNumber !== null && payment.status !== TenderStatus.PreTender,
    );
  }
  const lineTenders = activeTenders.map(payment => convertLineTender(payment));
  const lineRefunds = (cartResponse.refunds || []).map(refund => convertLineTender(refund, true));
  const lineItem: IElectronicJournalRecord[] = [
    ...(cartResponse.returns || []).map(element => convertLineItem(element, cartResponse, true)),
    ...(cartResponse.items || []).map(element => convertLineItem(element, cartResponse, false)),
  ];
  let linePromotions: IElectronicJournalRecord[] = [];
  (cartResponse.rewards || []).forEach(reward => {
    // RewardType for Proximity is IncentiveMessageReward' and 'Message' for cashier message
    if (
      reward.rewardType &&
      reward.rewardType !== PromotionRewardType.Message &&
      reward.rewardType !== PromotionRewardType.IncentiveMessageReward &&
      (reward.rewardType !== PromotionRewardType.StoredValue ||
        reward.promotionDetails?.rewardAsTender) &&
      reward.promotionDetails?.isDisplayEnabled !== false
    ) {
      linePromotions.push(convertLinePromotion(reward));
    }
  });
  linePromotions = promotionItemUOM(linePromotions, lineItem);
  const lineCoupons = (cartResponse.coupons || []).map(convertLineCoupon);

  let records = lineItem
    .concat(lineTenders)
    .concat(lineRefunds)
    .concat(lineCoupons);

  const { sortBySequence } = getCurrentXLRConfig();

  if (sortBySequence) {
    records = records.sort((a, b) => {
      const aSequence = BigInt(a.Sequence ?? '0');
      const bSequence = BigInt(b.Sequence ?? '0');
      return aSequence > bSequence ? 1 : bSequence > aSequence ? -1 : 0;
    });
  } else {
    records = records.sort((a, b): number => a.SequenceNumber - b.SequenceNumber);
  }

  splicePromotions(linePromotions, records);

  const lineTaxExemptions = cartResponse.cartInfo?.totals?.taxAmounts?.taxExemptAmount
    ? [convertLineTaxExemption(cartResponse.cartInfo.totals.taxAmounts.taxExemptAmount)]
    : [];

  const itemTaxFees = cartResponse.cartInfo?.totals?.taxAmounts?.itemFee
    ? [convertLineItemFee(cartResponse.cartInfo.totals.taxAmounts.itemFee)]
    : [];

  const transactionFee = cartResponse.cartInfo?.totals?.taxAmounts?.transactionFee
    ? [convertTransactionFee(cartResponse.cartInfo.totals.taxAmounts.transactionFee)]
    : [];

  const tenderEligibilityList = cartResponse.cartInfo?.totals?.tenderEligibleAmounts
    ? covertedLineTenderEligibility(cartResponse.cartInfo?.totals?.tenderEligibleAmounts)
    : [];

  const newRecords = lineTaxExemptions
    .concat(itemTaxFees)
    .concat(transactionFee)
    .concat(tenderEligibilityList)
    .concat(records);

  return newRecords;
};

export const splicePromotions = (
  linePromotions: IElectronicJournalRecord[],
  records: IElectronicJournalRecord[],
): IElectronicJournalRecord[] => {
  linePromotions.forEach(promotion => {
    //First step is find the last item the promotion is linked to
    if (promotion.SequenceNumber === 0) {
      let linkedLineIndex: number = -1;
      linkedLineIndex = mapPromotions(linkedLineIndex, promotion, records);
      if (linkedLineIndex < 0) {
        linkedLineIndex = records.length;
      }
      promotion.Price =
        promotion.Type === EJRecordTypes.RewardAsTender
          ? `${promotion.Price}`
          : `-${promotion.Price}`;

      records.splice(linkedLineIndex + 1, 0, promotion);
    } else {
      records.splice(promotion.SequenceNumber - 1, 0, promotion);
    }
  });

  return records;
};

const mapPromotions = (
  linkedLineIndex: number,
  promotion: IElectronicJournalRecord,
  records: IElectronicJournalRecord[],
): number => {
  promotion.EligibilityInfo.forEach(item => {
    if (item.SequenceNumber) {
      const recordNumber = records.findIndex(
        record =>
          record.Type === EJRecordTypes.Product &&
          record.SequenceNumber === Number(item.SequenceNumber),
      );
      if (linkedLineIndex < recordNumber) {
        linkedLineIndex = recordNumber;
      }
      item.Description = records[recordNumber]?.Description ?? '';
    }
  });
  return linkedLineIndex;
};

const promotionItemUOM = (
  linePromotions: IElectronicJournalRecord[],
  lineItems: IElectronicJournalRecord[],
): IElectronicJournalRecord[] => {
  linePromotions.forEach(reward => {
    reward.EligibilityInfo.forEach(item => {
      if (!item.UOM) {
        item.UOM = lineItems.find(
          product => product.SequenceNumber === item.SequenceNumber,
        )?.UnitOfMeasureCode;
      }
      if (!item.UOM) {
        item.UOM = store
          .getState()
          .itemList.ejItems.find(
            product => product.SequenceNumber === item.SequenceNumber,
          )?.UnitOfMeasureCode;
      }
    });
  });
  return linePromotions;
};

export const convertLineItem = (
  lineItemVal: CartItemViewData,
  cartResponse: CartResponse,
  isReturn: boolean,
): IElectronicJournalRecord => {
  const tareWeight: ITareWeight = {
    TareValue: lineItemVal.lineItem?.quantity?.weightQuantity?.tare?.tareValue,
    IsManualPercentage: lineItemVal.lineItem?.quantity?.weightQuantity?.tare?.isManualPercentage,
    IsManualFixedTare: lineItemVal.lineItem?.quantity?.weightQuantity?.tare?.isManualFixedTare,
    GrossWeight: lineItemVal.lineItem?.quantity?.weightQuantity?.tare?.grossWeight,
    IsTareOverride: lineItemVal?.lineItem?.quantity?.weightQuantity?.tare?.isTareOverride,
  };

  const linkedCartItems = getLinkedItems(lineItemVal, cartResponse, isReturn);
  const linkedLines = mapLineItems(linkedCartItems);

  let price: string = convertCurrency(0, undefined, false);
  let unitPrice: string | undefined;
  let OverrideUnitPrice: string | undefined;
  let isMsu: boolean | undefined;
  let type: EJRecordTypes;
  let taxes: Partial<ItemTaxViewData>[] | undefined;
  let onlineItem: OnlineItem | undefined;

  const currencyCode = lineItemVal.price?.currency;

  if (lineItemVal.amount !== undefined) {
    price = convertCurrency(lineItemVal.amount, currencyCode, false);
  }

  type = lineItemVal.status === 'Returned' ? EJRecordTypes.ReturnItem : EJRecordTypes.Product;
  // This is a temporary fix to show returns in selling service
  if (isReturn) {
    type = EJRecordTypes.ReturnItem;
  }

  const lineItemPriceModifierNewPrice = lineItemVal.lineItem?.priceModifier?.newPrice ?? 0;
  const lineItemPriceModifierAmount = lineItemVal.lineItem?.priceModifier?.amount ?? 0;
  if (lineItemVal.price) {
    unitPrice = convertUnitPriceCurrency(
      lineItemVal.price.unitPrice,
      lineItemVal.price.currency,
      lineItemVal.price.quantity,
      lineItemVal.lineItem?.quantity && lineItemVal.lineItem?.quantity.unitOfMeasure,
    );
    isMsu = (lineItemVal.price.quantity || 1) > 1;
    if (
      lineItemVal.lineItem?.priceModifier &&
      lineItemVal.lineItem.priceModifier.totalPriceDifference &&
      (lineItemVal.lineItem.priceModifier.method === 'PriceOverride' ||
        lineItemVal?.lineItem?.priceModifier?.method === 'EmbeddedPrice')
    ) {
      OverrideUnitPrice = convertUnitPriceCurrency(
        lineItemPriceModifierNewPrice + lineItemPriceModifierAmount,
        lineItemVal.price.currency,
        lineItemVal.price.quantity,
        lineItemVal.lineItem?.quantity && lineItemVal.lineItem?.quantity.unitOfMeasure,
      );
      unitPrice = convertUnitPriceCurrency(
        lineItemPriceModifierNewPrice,
        lineItemVal.regularPrice?.currency,
        lineItemVal.regularPrice?.quantity,
        lineItemVal.lineItem?.quantity && lineItemVal.lineItem?.quantity.unitOfMeasure,
      );
    }

    if (lineItemVal.lineItem?.packQuantity) {
      const { packQuantity } = lineItemVal.lineItem;

      unitPrice = convertUnitPriceCurrency(
        packQuantity.unitPrice || 0,
        lineItemVal.price.currency,
        packQuantity.value || 0,
        lineItemVal.lineItem?.quantity && lineItemVal.lineItem?.quantity.unitOfMeasure,
      );
    }
  }

  if (lineItemVal.taxes) {
    taxes = lineItemVal.taxes;
  }

  if (lineItemVal.additionalData) {
    onlineItem = {
      authorizationExternalId: lineItemVal.additionalData?.onlineItem?.authorizationExternalId,
      initialBalance: lineItemVal.additionalData?.onlineItem?.initialBalance,
      currentBalance: lineItemVal.additionalData?.onlineItem?.currentBalance,
      serviceType: lineItemVal.additionalData.onlineItem?.serviceType,
    };
  }

  const ej: IElectronicJournalRecord = {
    Type: type,
    SequenceNumber: getValueAsNumber(lineItemVal.lineId),
    Name: lineItemVal.description ?? '',
    Description: lineItemVal.fullDescription,
    Price: price,
    PriceModifier: lineItemVal.lineItem?.priceModifier?.method,
    RegularPrice: lineItemVal.regularPrice,
    ExtendedAmount:
      lineItemVal.extendedAmount || lineItemVal.extendedAmount === 0
        ? convertCurrency(lineItemVal.extendedAmount, undefined, false)
        : '',
    RawPrice: lineItemVal.amount || 0,
    UnitPrice: unitPrice,
    Taxes: taxes,
    Voided: lineItemVal.status === LineItemStatus.Cancelled,
    Update: lineItemVal.status === LineItemStatus.Updated,
    Selected: false,
    ItemId: lineItemVal.lineItem?.itemId?.value,
    Quantity: 1,
    UnitOfMeasureCode: UnitOfMeasureTypes.Each,
    IsMsu: isMsu,
    OverrideUnitPrice: OverrideUnitPrice,
    EligibilityInfo: [],
    Indicators: lineItemVal.itemIndicators,
    ItemLink: getValueAsNumber(lineItemVal.parentLineId),
    LinkedLines: linkedLines,
    TareWeight: tareWeight,
    OriginalQuantity: 1,
    OnlineItem: onlineItem,
    PackQuantity: lineItemVal.lineItem?.packQuantity,
    Sequence: lineItemVal.sequence ?? '0',
    NumericUnitPrice: getNumericUnitPrice(
      lineItemVal,
      lineItemPriceModifierNewPrice,
      lineItemPriceModifierAmount,
    ),
  };

  if (lineItemVal.lineItem?.quantity) {
    ej.Quantity = lineItemVal.lineItem?.quantity.unitOfMeasure
      ? lineItemVal.lineItem?.quantity.units || lineItemVal.lineItem?.quantity.value
      : lineItemVal.lineItem?.quantity.value;
    ej.QuantityValue = lineItemVal.lineItem?.quantity.value;
    ej.UnitOfMeasureCode = lineItemVal.lineItem?.quantity.unitOfMeasure || UnitOfMeasureTypes.Each;
  }
  ej.OriginalQuantity = lineItemVal.originalQuantity ?? ej.Quantity;

  if (lineItemVal.itemIndicators) {
    ej.Indicators = lineItemVal.itemIndicators;
    if (ej.Indicators?.includes('S')) {
      ej.isSnapItem = true;
    }
  }

  return ej;
};

const getLinkedItems = (
  lineItem: CartItemViewData,
  cartResponse: CartResponse,
  isReturn: boolean,
): CartItemViewData[] => {
  const cartItems = isReturn ? cartResponse.returns : cartResponse.items;

  if (!lineItem.linkedLines) {
    lineItem.linkedLines = [];
  }
  cartItems?.forEach(item => {
    if (
      lineItem.lineId === item.parentLineId &&
      !lineItem.linkedLines?.some(linkedLine => linkedLine.lineId === item.lineId)
    ) {
      lineItem.linkedLines?.push(item);
    }
  });
  return lineItem.linkedLines;
};

export const convertLineTender = (
  lineTender: CartPaymentData,
  isRefund = false,
): IElectronicJournalRecord => {
  const displayAmount =
    lineTender.change && (!lineTender.amount || lineTender.tenderId !== lineTender.tenderChangeId)
      ? lineTender.change
      : lineTender.amount;
  const tenderCurrencySymbol = lineTender.currencySymbol || LOCAL_CURRENCY_SYMBOL;

  const price = `${tenderCurrencySymbol}${(displayAmount || 0).toFixed(2)}`;

  const ejRecord: IElectronicJournalRecord = {
    Type: isRefund ? EJRecordTypes.Refund : EJRecordTypes.Tender,
    SequenceNumber: lineTender.sequenceNumber ?? 0,
    Name: lineTender.tenderName ?? '',
    Price: price,
    Voided: lineTender.status === LineItemStatus.Cancelled,
    Update: lineTender.status === LineItemStatus.Updated,
    Selected: false,
    TenderType: getTenderType(lineTender),
    TenderReferences: getTenderReferences(lineTender),
    EligibilityInfo: [],
    ItemLink: lineTender.itemLink,
    Sequence: lineTender.sequence ?? '0',
    lineId: lineTender.lineId,
  };

  if (lineTender.epsData !== undefined) {
    ejRecord.IsProcessedByEps = true;
    ejRecord.EpsData = {
      authCode: lineTender.epsData?.authCode,
      paymentTranId: lineTender.epsData?.paymentTranId,
      referenceId: lineTender.epsData?.referenceId,
      paymentSessionId: lineTender.epsData?.paymentSessionId,
      amountDue: lineTender.amount,
      tenderId: lineTender.tenderId,
    };
  }

  return ejRecord;
};

export const convertLinePromotion = (linePromotion: CartRewardData): IElectronicJournalRecord => {
  const price = linePromotion.promotionDetails?.rewardAsTender
    ? convertCurrency(linePromotion.rewardAmount || 0, undefined, true)
    : convertCurrency(linePromotion.rewardAmount || 0, undefined, false);

  const eligibilities = linePromotion.rewardedItems?.map(
    (rewardedItem): IEligibilityInfo => ({
      Quantity: rewardedItem?.quantity?.value ?? 0,
      NetPrice: convertCurrency(rewardedItem.rewardAmount),
      Description: rewardedItem.description || '',
      SequenceNumber: rewardedItem.lineId,
      UOM: rewardedItem.quantity.unitOfMeasure,
    }),
  );
  const description =
    linePromotion.promotionDescription || linePromotion.promotionDetails?.tenderName || '';
  const EjRecord: IElectronicJournalRecord = {
    Quantity:
      !linePromotion.promotionDetails?.rewardAsTender &&
      linePromotion.rewardType?.toString() === 'Coupon'
        ? 1
        : undefined,
    Type: linePromotion.promotionDetails?.rewardAsTender
      ? EJRecordTypes.RewardAsTender
      : linePromotion.rewardType?.toString() === 'Coupon'
      ? EJRecordTypes.IssuedCoupon
      : EJRecordTypes.Promotion,
    SequenceNumber: getValueAsNumber(linePromotion.lineId),
    Description: description,
    Name: description || linePromotion.rewardType || '',
    Price: price,
    RawPrice: linePromotion.rewardAmount || 0,
    Voided: linePromotion.status === LineItemStatus.Cancelled,
    Update: linePromotion.status === LineItemStatus.Updated,
    Selected: false,
    EligibilityInfo: eligibilities ?? [],
    Savings: convertCurrency(Math.abs(linePromotion.rewardAmount || 0)),
    ItemLink: linePromotion.itemLink,
    PromotionId: linePromotion.promotionId,
    CouponId: linePromotion.promotionDetails?.couponId,
    IsEndOfTicketPromotion: linePromotion.promotionDetails?.isEndOfTicketPromotion,
    pinned: false,
    Sequence: '0',
  };
  return EjRecord;
};

export const convertLineCoupon = (lineCoupon: CartCouponData): IElectronicJournalRecord => {
  const redemptionState: IRedemptionState = {
    Redeemed: lineCoupon.redeptionStates?.redeemed,
    Unused: lineCoupon.redeptionStates?.unused,
  };
  return {
    Type: EJRecordTypes.Coupon,
    SequenceNumber: lineCoupon.sequenceNumber ?? 0,
    Sequence: lineCoupon.sequence ?? '0',
    Name: lineCoupon.couponId,
    CouponId: lineCoupon.couponId,
    Selected: false,
    Quantity: lineCoupon.quantity,
    CouponRedemptionState: redemptionState,
    Voided: lineCoupon.status === LineItemStatus.Cancelled,
    Update: lineCoupon.status === LineItemStatus.Updated,
    EligibilityInfo: [],
    CouponRedemptionsIds: lineCoupon.couponRedemptionIds,
    Price: convertCurrency(lineCoupon.Amount || 0, undefined, false),
    ItemLink: lineCoupon.itemLink,
  };
};

const covertedLineTenderEligibility = (
  tenders: TenderEligibleAmounts[],
): IElectronicJournalRecord[] => {
  const ejRecords: IElectronicJournalRecord[] = tenders.map(
    (tender: TenderEligibleAmounts): IElectronicJournalRecord => ({
      Type: EJRecordTypes.TenderEligibility,
      SequenceNumber: Number(tender.tenderId),
      Sequence: '0',
      Name: getTenderName(tender),
      EligibilityInfo: [],
      Price: convertCurrency(tender.amount || 0),
      Selected: false,
      Voided: false,
      TenderType: getTenderType(tender),
      TenderReferences: getTenderReferences(tender),
      pinned: true,
    }),
  );

  return ejRecords;
};

const convertLineTaxExemption = (exemptedTax: number): IElectronicJournalRecord => {
  return {
    Type: EJRecordTypes.TaxExemption,
    SequenceNumber: 0,
    Sequence: '0',
    Name: 'TaxExemptionLine_Name',
    Price: convertCurrency(-exemptedTax, undefined, false),
    Savings: convertCurrency(exemptedTax, undefined, false),
    Voided: false,
    Selected: false,
    EligibilityInfo: [],
    pinned: true,
  };
};

const convertLineItemFee = (itemFee: number): IElectronicJournalRecord => {
  return {
    Type: EJRecordTypes.ItemFee,
    SequenceNumber: 0,
    Sequence: '0',
    Name: 'Item_Fee_Total',
    Price: convertCurrency(itemFee, undefined, false),
    Voided: false,
    Selected: false,
    EligibilityInfo: [],
    pinned: true,
  };
};

const convertTransactionFee = (transactionFee: number): IElectronicJournalRecord => {
  return {
    Type: EJRecordTypes.TransactionFee,
    SequenceNumber: 0,
    Name: 'Transaction_Fee_Total',
    Price: convertCurrency(transactionFee, undefined, false),
    Voided: false,
    Selected: false,
    EligibilityInfo: [],
    pinned: true,
    Sequence: '0',
  };
};

export const getTenderReferences = (lineTender: CartPaymentData): string[] => {
  let tenderReferences: string[] = [];
  const tenders = store.getState().tenderMenu.sellableTenders.rawTenders;
  if (tenders) {
    const tender = tenders.find((t): boolean => t.id === lineTender.tenderId);
    if (tender && tender.tenderReferences) {
      tenderReferences = tender.tenderReferences;
    }
  }
  return tenderReferences;
};

export const getTenderName = (lineTender: CartPaymentData): string => {
  let tenderName: string = '';
  const tenders = store.getState().tenderMenu.sellableTenders.rawTenders;
  const tender = tenders.find((t): boolean => t.id === lineTender.tenderId);
  if (tender?.description) {
    tenderName = tender?.description;
  }
  return tenderName;
};

export const getTenderType = (lineTender: CartPaymentData): TenderType => {
  let tenderType = TenderType.None;
  const tenders = store.getState().tenderMenu.sellableTenders.rawTenders;
  if (tenders) {
    const tender = tenders.find((t): boolean => t.id === lineTender.tenderId);
    if (tender && tender.type) {
      tenderType = tender.type;
    }
  }
  return tenderType;
};

const mapLineItems = (items?: CartItemViewData[]): ILineItem[] => {
  return (items || []).map(
    (item: CartItemViewData): ILineItem => ({
      ItemIndicators: item.itemIndicators,
      Description: item.description,
      LongDescription: item.fullDescription,
      ItemLink: getValueAsNumber(item.parentLineId),
      PriceModifiersReasonCode: item.priceModifierReasonCode,
      ItemID: item.lineItem?.itemId?.value,
      ItemIdType: item.lineItem?.itemId?.type,
      ItemType: item.parentLineLinkType ? item.parentLineLinkType : item.itemType,
      EntryMethod: item.lineItem?.EntryMethod,
      SequenceNumber: getValueAsNumber(item.lineId),
      Amount: item.amount !== undefined ? item.amount : item.extendedAmount,
      ActualSaleUnitPrice: mapSaleUnitPrice(item.price),
      RegularSaleUnitPrice: mapSaleUnitPrice(item.regularPrice ? item.regularPrice : item.price),
      Status: item.status as LineItemStatus,
      Rewards: {
        Rewards: item.rewards?.map(
          (item): ILineReward => ({
            Amount: item.amount,
            RewardMethod: item.method,
            RewardPercentage: item.percentage,
            UnitPriceWithoutReward: item.priceWithoutReward,
            PromotionMessage: item.promotionMessage,
          }),
        ),
      },
      GiftReceiptCount: item.giftReceiptCount,
      SecurityScaleRequired: item.securityScale,
      IsInActive: item.isInactive,
      ImageUrl: item.imageUrl,
      ItemFees: item.taxes?.map(
        (item: ItemTaxViewData): ITax => ({
          Amount: item.amount,
          Description: item.description,
          ExemptAmount: item.exemptAmount,
          TaxRuleID: item.code,
        }),
      ),
      Quantity: item.lineItem?.quantity && {
        UnitOfMeasureCode: item.lineItem?.quantity.unitOfMeasure,
        Units: item.lineItem?.quantity.units,
        Value: item.lineItem?.quantity.value,
        EntryMethod: item.lineItem?.quantity.entryMethod,
        UnitPrice: item.lineItem?.quantity.unitPrice,
      },
      LineModifiers: item.lineModifiers,
      CancelReason: item.cancelReason,
      LinkedLines: mapLineItems(item.linkedLines),
      PriceModifier: mapPriceModifier(item.lineItem?.priceModifier),
      TareWeight: mapTareValues(item.lineItem?.quantity?.weightQuantity?.tare),
    }),
  );
};

const mapSaleUnitPrice = (saleUnitPrice?: PriceViewData): IPricedQuantity | undefined => {
  if (saleUnitPrice) {
    return {
      Price: {
        Value: saleUnitPrice.unitPrice,
        Currency: saleUnitPrice.currency,
      },
      Quantity: saleUnitPrice.quantity,
    };
  }
};

const mapPriceModifier = (
  priceModifier?: PriceModifierViewData,
): IPriceModifierInfo | undefined => {
  if (!priceModifier) {
    return undefined;
  }
  return {
    NewPrice: priceModifier.newPrice,
    Amount: priceModifier.amount,
    Percentage: priceModifier.percentage,
    CalculatedTotalPrice: priceModifier.newTotalPrice,
    TotalPriceDifference: priceModifier.totalPriceDifference,
  };
};

const mapTareValues = (tare?: TareQuantity): ITareWeight | undefined => {
  if (tare) {
    return {
      TareValue: tare?.tareValue,
      IsTareOverride: tare.isTareOverride,
      IsManualFixedTare: tare.isManualFixedTare,
      IsManualPercentage: tare.isManualPercentage,
      GrossWeight: tare.grossWeight,
    };
  }
};

const getNumericUnitPrice = (
  lineItemVal: CartItemViewData,
  lineItemPriceModifierNewPrice: number,
  lineItemPriceModifierAmount: number,
): number => {
  let numericUnitPrice: number;
  if (lineItemVal.price) {
    numericUnitPrice =
      (lineItemVal.price?.unitPrice || 0) +
      (lineItemVal.linkedLines?.map(item => item.amount || 0).reduce((a, b) => a + b, 0) || 0);
  } else {
    numericUnitPrice =
      (lineItemVal.regularPrice?.unitPrice || 0) +
      (lineItemVal.linkedLines?.map(item => item.amount || 0).reduce((a, b) => a + b, 0) || 0);
  }

  if (lineItemVal.lineItem?.packQuantity) {
    const priceModifier = lineItemVal.lineItem?.priceModifier;
    if (priceModifier) {
      //pack of unit
      numericUnitPrice = lineItemPriceModifierNewPrice + lineItemPriceModifierAmount;
    } else if (lineItemVal.lineItem.packQuantity?.unitPrice) {
      //pack of weight
      numericUnitPrice = lineItemVal.lineItem.packQuantity.unitPrice;
    }
  }

  return numericUnitPrice;
};
